import { autoScrollWindowForElements } from '@atlaskit/pragmatic-drag-and-drop-auto-scroll/element'
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { useEffect, useRef } from 'react'
import invariant from 'tiny-invariant'

interface Id {
  id: string
}
interface ReorderableItemMonitorContainerProps<T extends Id> {
  scrollableDataType: string
  lowestOrder: number
  highestOrder: number
  orderedItems: T[]
  handleReorderItem: (itemId: string, newOrder: number) => void
}

function extractOrder(data: Record<string, unknown>) {
  const { order } = data
  invariant(typeof order === 'number')
  return order
}

function extractId(data: Record<string, unknown>) {
  const { id } = data
  invariant(typeof id === 'string')
  return id
}

/**
 * Custom hook that sets up auto-scrolling and drag-and-drop functionality for reordering items.
 * Returns a ref to the container element.
 */
export function useReorderableItemMonitorContainer<T extends Id>({
  scrollableDataType,
  lowestOrder,
  highestOrder,
  orderedItems,
  handleReorderItem,
}: ReorderableItemMonitorContainerProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null)

  /**
   * Set up auto-scrolling functionality to enhance the drag-and-drop user experience.
   *
   * This useEffect sets up automatic scrolling when a table row (draggable item) approaches the edges
   * of the viewport so users can drag items across large distances without manually scrolling.
   *
   * The source data is defined in LeftNavReorderableItem.tsx in the draggable setup getInitialData prop.
   */
  useEffect(() => {
    const cleanUpAutoScroll = autoScrollWindowForElements({
      canScroll: ({ source }) => source.data.type === scrollableDataType,
    })

    // Return a cleanup function that removes the auto-scroll behavior when the component unmounts,
    // preventing potential memory leaks or unintended behavior on other pages or components.
    return () => {
      cleanUpAutoScroll()
    }
  }, [scrollableDataType])

  useEffect(() => {
    const container = containerRef.current
    invariant(container)

    const cleanupMonitor = combine(
      monitorForElements({
        onDrop({ source, location }) {
          if (location.current.dropTargets.length === 0) {
            return
          }

          const target = location.current.dropTargets[0]
          if (source.data.id === target.data.id) {
            return
          }

          if (source.data.type !== scrollableDataType) {
            return
          }

          const closestEdgeOfTarget = extractClosestEdge(target.data)
          invariant(closestEdgeOfTarget === 'top' || closestEdgeOfTarget === 'bottom')

          const itemId = extractId(source.data)
          const targetOrder = extractOrder(target.data)
          const oldOrder = extractOrder(source.data)

          // Start with target's order
          let newOrder = targetOrder

          if (closestEdgeOfTarget === 'bottom') {
            // Dropping below the target item
            if (targetOrder !== highestOrder) {
              if (oldOrder > targetOrder) {
                newOrder = targetOrder + 1
              }
            }
          } else {
            if (targetOrder === oldOrder + 1) {
              // Handle a specific edge case of dragging downward but not actually changing places
              newOrder = oldOrder
            }
          }

          if (newOrder === oldOrder) {
            // If the new order is the same as the old order, no need to call the reorder mutation
            return
          }

          // Find the dragged item details
          const draggedItem = orderedItems.find((item) => item.id === itemId)
          if (!draggedItem) {
            return
          }

          handleReorderItem(itemId, newOrder)
        },
      }),
    )

    return () => {
      // Call the cleanup function when the component unmounts
      cleanupMonitor()
    }
  }, [orderedItems, lowestOrder, highestOrder, handleReorderItem, scrollableDataType])

  return containerRef
}
